import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { usePositionSearch, SearchResult } from '../../hooks/usePositionSearch';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const HelperText = styled.p`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 500px;
  overflow-y: auto;
  flex: 1;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const ResultsHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const ResultCard = styled.div`
  padding: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
  }
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const TokenLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.05);
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const TokenName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TokenSymbol = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const MatchType = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-transform: capitalize;
`;

const ResultDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const DetailRow = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  font-family: 'Plus Jakarta Sans', sans-serif;
  
  &.highlight {
    color: #60a5fa;
  }
  
  &.warning {
    color: #fbbf24;
  }
`;

const MonoText = styled.span`
  font-family: 'Courier New', monospace;
  color: rgba(255, 255, 255, 0.6);
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const ErrorState = styled.div`
  padding: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #ef4444;
  font-size: 12px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

interface PositionSearchProps {
  onPositionSelect?: (position: SearchResult) => void;
}

export const PositionSearch: React.FC<PositionSearchProps> = ({ onPositionSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { search, results, searching, error, clearSearch } = usePositionSearch();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim()) {
        search(searchTerm);
      } else {
        clearSearch();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSelect = (result: SearchResult) => {
    onPositionSelect?.(result);
  };

  return (
    <Container>
      <SearchSection>
        <Label>Search Position</Label>
        <SearchInput
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Token name, NFT ID, or contract address"
        />
        <HelperText>
          Search by token name, symbol, NFT Token ID, or contract address
        </HelperText>
      </SearchSection>

      {searching && (
        <LoadingState>Searching...</LoadingState>
      )}

      {error && (
        <ErrorState>{error}</ErrorState>
      )}

      {results.length > 0 && (
        <ResultsContainer>
          <ResultsHeader>
            Found {results.length} result{results.length !== 1 ? 's' : ''}
          </ResultsHeader>
          
          {results.map((result) => (
            <ResultCard
              key={result.contract_address}
              onClick={() => handleSelect(result)}
            >
              <ResultRow>
                {result.logo_url && (
                  <TokenLogo
                    src={result.logo_url}
                    alt={result.symbol}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/token-placeholder.png';
                    }}
                  />
                )}
                <TokenInfo>
                  <TokenName>{result.name}</TokenName>
                  <TokenSymbol>{result.symbol}</TokenSymbol>
                  <MatchType>Matched by: {result.matchType}</MatchType>
                </TokenInfo>
              </ResultRow>

              <ResultDetails>
                {result.contract_address && (
                  <DetailRow>
                    Contract: <MonoText>{result.contract_address}</MonoText>
                  </DetailRow>
                )}
                {result.pool_address && (
                  <DetailRow>
                    Pool: <MonoText>{result.pool_address}</MonoText>
                  </DetailRow>
                )}
                {result.nft_token_id ? (
                  <DetailRow className="highlight">
                    NFT Position ID: <MonoText>#{result.nft_token_id}</MonoText>
                  </DetailRow>
                ) : (
                  <DetailRow className="warning">
                    No NFT position found for this token
                  </DetailRow>
                )}
              </ResultDetails>
            </ResultCard>
          ))}
        </ResultsContainer>
      )}

      {!searching && !error && searchTerm && results.length === 0 && (
        <EmptyState>No results found</EmptyState>
      )}
    </Container>
  );
};

